import { FundingNeedResponse } from '@/models/FundingNeeds'
import {
  ListItem,
  ListItemText,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Box,
  LinearProgress,
} from '@mui/material'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useScanTask } from '@/utils/tasks/scanTask'
import { useAdoptActiveScan } from '@/utils/tasks/useAdoptActiveScan'
import { useLatestScanResultsByNeedId } from '@/utils/hooks/scans'
import { ScanResponse } from '@/models/Scans'
import UpgradeCTA from './UpgradeCTA'
import { useFundingNeedStaleness } from '@/utils/hooks/useFundingNeedStaleness'
import {
  ScanStatusAnnouncer,
  scanAnnouncement,
} from './ScanStatusAnnouncer'
import { ScanBlockedTooltip } from './ScanBlockedTooltip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { AccessibleTooltip } from './AccessibleTooltip'

interface MatchNeedsListItemProps {
  token: string
  fundingNeed: FundingNeedResponse
  scanIds: number[] | undefined
  runScanOnAll: boolean
  isAnnualSubscriber?: boolean
}

export default function MatchNeedsListItem({
  token,
  fundingNeed,
  scanIds,
}: MatchNeedsListItemProps) {
  const includeRelational = true
  const [isStartingScan, setIsStartingScan] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState('')

  getCurrentUser() // Not using any values from this hook

  const {
    hasResults: hasExistingResults,
    canStartScan,
    scanBlockedReason,
    showStaleWarning,
    viewResultsDisabled,
    warningMessage: staleWarningMessage,
    latestScanFoundNothing,
  } = useFundingNeedStaleness(token, fundingNeed, scanIds)

  const { data, isLoading } = useLatestScanResultsByNeedId(
    token as string,
    Number(fundingNeed.id),
  ) as {
    data: ScanResponse[]
    isLoading: boolean
    isError: boolean
  }

  const {
    startScan,
    isLoadingForNeed,
    errorForNeed,
    progressForNeed,
    statusForNeed,
    latestStatusForNeed,
    hasActiveScan,
    isScanStaleForNeed,
    refreshScanState,
  } = useScanTask({
    token: token as string,
    // Fires for every scan that finishes, not just this row's.
    onCompleted: (_results, _taskId, completedNeedId) => {
      if (completedNeedId === String(fundingNeed.id)) setIsStartingScan(false)
    },
    // Fires for every scan that fails, not just this row's.
    onError: (error, _taskId, erroredNeedId) => {
      if (erroredNeedId !== String(fundingNeed.id)) return
      // Reset starting state
      setIsStartingScan(false)

      // Check if this is a payment required error
      if (error.name === 'PaymentRequired') {
        setUpgradeReason(error.message)
        setShowUpgradeDialog(true)
      }
    },
  })

  // Use useEffect to refresh scan state after navigation
  useEffect(() => {
    // Force refresh scan state when component mounts
    refreshScanState()
  }, [refreshScanState])

  // Get UI state from the task hook
  const needId = fundingNeed.id ? String(fundingNeed.id) : ''

  // Without this the row only knows about scans this browser started: one started
  // on another device, by a colleague or during onboarding renders as an idle
  // "Re-find Matches" button that POSTs straight into a 409.
  useAdoptActiveScan(token as string, Number(fundingNeed.id))

  // Check if scan is stale
  const isStale = needId ? isScanStaleForNeed(needId) : false

  // Is actively scanning (not stale)
  const isScanning = needId
    ? (hasActiveScan(needId) || isLoadingForNeed(needId) || isStartingScan) &&
      !isStale
    : false

  const scanError = needId ? errorForNeed(needId) : null

  // Scan progress info
  const scanProgress = needId
    ? {
        progress: progressForNeed(needId) || 0,
        message: isStale
          ? 'Scan appears to be stuck...'
          : statusForNeed(needId) === 'running'
            ? 'Matching...'
            : isStartingScan
              ? 'Starting scan...'
              : 'Initializing...',
      }
    : { progress: 0, message: '' }

  const isScanComplete =
    (needId && latestStatusForNeed(needId) === 'completed') ||
    (data && data.length > 0)
      ? true
      : false

  const hasMatches = needId
    ? data && data.length > 0 && data.some((result) => Number(result.rank) > 3)
    : false

  async function handleRunScan(
    needId: string,
    includeRelational: boolean,
    includeFuturePlanning = false,
  ) {
    if (!token || !needId) return
    // The scan buttons are disabled with this reason; this also stops a
    // click on the span-rendered buttons, which `disabled` does not.
    if (scanBlockedReason) return

    try {
      setIsStartingScan(true)

      // Start the scan using the hook
      const jobId = await startScan(
        needId as string,
        includeRelational,
        includeFuturePlanning,
      )

      if (!jobId) {
        // If no job ID returned but no error thrown, must be already scanning
        setIsStartingScan(false)
      }
    } catch {
      setIsStartingScan(false)
    }
  }

  return (
    <ListItem>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {fundingNeed.title}
            {fundingNeed.is_archived && (
              <Chip
                label="Archived"
                size="small"
                sx={{
                  ml: 1,
                  height: '20px',
                  fontSize: '11px',
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                }}
              />
            )}
          </Box>
        }
      />

      {!hasMatches && !isLoading && data && data.length > 0 ? (
        <Alert severity="info" sx={{ m: 0.5 }}>
          Sorry, no matches found
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Column 1: warning icon - fixed width, reserved on every row */}
        <Box
          sx={{
            width: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {hasExistingResults && staleWarningMessage && !isScanning && (
            <AccessibleTooltip
              title={staleWarningMessage}
              arrow
              placement="top"
            >
              <span
                role="img"
                aria-label={`Warning: ${staleWarningMessage}`}
                tabIndex={0}
              >
                <WarningAmberIcon
                  sx={{ color: 'warning.main', fontSize: '22px' }}
                />
              </span>
            </AccessibleTooltip>
          )}
        </Box>

        {/* Column 2: View Results - fixed width, reserved on every row.
            Disabled (not hidden) for stale results so a re-scan is required
            first — a tooltip points them at Re-find Matches. Stays enabled
            as a fallback when the org can't currently re-scan (paused/at
            cap), since it's the only way back to their existing matches. */}
        <Box sx={{ width: '140px', flexShrink: 0 }}>
          {hasExistingResults &&
            (viewResultsDisabled ? (
              <AccessibleTooltip
                title="Please click Re-find Matches button to see latest matches"
                arrow
                placement="top"
              >
                <span style={{ display: 'inline-block', width: '100%' }}>
                  <Button
                    variant="outlined"
                    disableRipple
                    disabled
                    aria-label={`View results for ${fundingNeed.title} (disabled — results are out of date)`}
                    sx={{
                      color: '#1C3045',
                      borderColor: '#1C3045',
                      border: 2,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      m: 0.5,
                      width: 'calc(100% - 8px)',
                    }}
                  >
                    View Results
                  </Button>
                </span>
              </AccessibleTooltip>
            ) : (
              <Button
                variant="outlined"
                disableRipple
                component={Link}
                to={`/match/${fundingNeed.id}`}
                aria-label={`View results for ${fundingNeed.title}`}
                sx={{
                  color: '#1C3045',
                  borderColor: '#1C3045',
                  border: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  m: 0.5,
                  width: 'calc(100% - 8px)',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                View Results
              </Button>
            ))}
        </Box>

        {/* Column 3: primary action/status - fixed width, reserved on every row */}
        <Box sx={{ width: '175px', flexShrink: 0 }}>
          {hasExistingResults ? (
            // isScanning is checked before staleness (as the mobile row does):
            // gating progress on `showStaleWarning && isScanning` left a
            // non-stale request that is re-scanning with an empty action
            // column for the whole scan.
            isScanning ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  sx={{
                    m: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    color: '#1C3045',
                    borderRadius: 2,
                    justifyContent: 'center',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    width: 'calc(100% - 8px)',
                    height: '40px',
                  }}
                >
                  <CircularProgress
                    size={20}
                    sx={{ color: '#1C3045', mr: 1 }}
                  />
                  Matching...
                </Typography>
                <Box sx={{ width: '100%' }}>
                  <LinearProgress
                    variant="determinate"
                    value={scanProgress.progress || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mx: 1,
                      bgcolor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#1C3045',
                      },
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      color: '#666',
                      mt: 0.5,
                    }}
                  >
                    {scanProgress.progress || 0}% complete
                  </Typography>
                </Box>
              </Box>
            ) : scanError ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <Alert
                  severity="error"
                  sx={{
                    m: 0.5,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'calc(100% - 8px)',
                  }}
                >
                  Sorry, something went wrong!
                </Alert>
                <ScanBlockedTooltip reason={scanBlockedReason}>
                  <Button
                    variant="contained"
                    disableRipple
                    aria-label={`Try scanning again for ${fundingNeed.title}`}
                    disabled={!canStartScan}
                    onClick={() => {
                      handleRunScan(String(fundingNeed.id), includeRelational)
                    }}
                    sx={{
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      color: 'white',
                      bgcolor: '#1C3045',
                      boxShadow: 'none',
                      borderRadius: 2,
                      m: 0.5,
                      width: 'calc(100% - 8px)',
                      height: '40px',
                      '&:hover': { backgroundColor: '#162433' },
                    }}
                  >
                    Try Again
                  </Button>
                </ScanBlockedTooltip>
              </Box>
            ) : showStaleWarning ? (
              <ScanBlockedTooltip reason={scanBlockedReason}>
                <Button
                  variant="contained"
                  disableRipple
                  aria-label={`Re-find matches for ${fundingNeed.title}`}
                  disabled={!canStartScan}
                  onClick={() => {
                    handleRunScan(String(fundingNeed.id), includeRelational)
                  }}
                  sx={{
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    color: 'white',
                    bgcolor: '#1C3045',
                    boxShadow: 'none',
                    borderRadius: 2,
                    m: 0.5,
                    width: 'calc(100% - 8px)',
                    height: '40px',
                    '&:hover': { backgroundColor: '#162433' },
                  }}
                >
                  Re-find Matches
                </Button>
              </ScanBlockedTooltip>
            ) : latestScanFoundNothing ? (
              <Typography
                sx={{
                  m: 0.5,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#1C3045',
                  borderRadius: 2,
                  justifyContent: 'center',
                  width: 'calc(100% - 8px)',
                  height: '40px',
                }}
              >
                <InfoOutlinedIcon sx={{ color: '#1C3045', mr: 1, fontSize: '20px' }} />
                No matches found
              </Typography>
            ) : (
              <Typography
                sx={{
                  m: 0.5,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#1C3045',
                  borderRadius: 2,
                  justifyContent: 'center',
                  width: 'calc(100% - 8px)',
                  height: '40px',
                }}
              >
                <TaskAltIcon
                  sx={{ color: '#1C3045', mr: 1, fontSize: '20px' }}
                />
                Scan Complete
              </Typography>
            )
          ) : isScanning ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography
                sx={{
                  m: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#1C3045',
                  borderRadius: 2,
                  justifyContent: 'center',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  width: 'calc(100% - 8px)',
                  height: '40px',
                }}
              >
                <CircularProgress size={20} sx={{ color: '#1C3045', mr: 1 }} />
                Matching...
              </Typography>
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={scanProgress.progress || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    mx: 1,
                    bgcolor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#1C3045',
                    },
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    color: '#666',
                    mt: 0.5,
                  }}
                >
                  {scanProgress.progress || 0}% complete
                </Typography>
              </Box>
            </Box>
          ) : scanError ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Alert
                severity="error"
                sx={{
                  m: 0.5,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  width: 'calc(100% - 8px)',
                }}
              >
                Sorry, something went wrong!
              </Alert>
              <ScanBlockedTooltip reason={scanBlockedReason}>
                <Button
                  variant="contained"
                  disableRipple
                  aria-label={`Try scanning again for ${fundingNeed.title}`}
                  disabled={!canStartScan}
                  onClick={() => {
                    handleRunScan(String(fundingNeed.id), includeRelational)
                  }}
                  sx={{
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    color: 'white',
                    bgcolor: '#1C3045',
                    boxShadow: 'none',
                    borderRadius: 2,
                    m: 0.5,
                    width: 'calc(100% - 8px)',
                    height: '40px',
                    '&:hover': { backgroundColor: '#162433' },
                  }}
                >
                  Try Again
                </Button>
              </ScanBlockedTooltip>
            </Box>
          ) : latestScanFoundNothing && !showStaleWarning ? (
            <Typography
              sx={{
                m: 0.5,
                fontSize: '14px',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                color: '#1C3045',
                borderRadius: 2,
                justifyContent: 'center',
                width: 'calc(100% - 8px)',
                height: '40px',
              }}
            >
              <InfoOutlinedIcon sx={{ color: '#1C3045', mr: 1, fontSize: '20px' }} />
              No matches found
            </Typography>
          ) : !showStaleWarning &&
            (isScanComplete || (scanIds && scanIds.length > 0)) ? (
            // A just-finished scan reads as complete only while the request is still
            // current; once it's edited or goes stale the scan button comes back.
            <Typography
              sx={{
                m: 0.5,
                fontSize: '14px',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                color: '#1C3045',
                borderRadius: 2,
                justifyContent: 'center',
                width: 'calc(100% - 8px)',
                height: '40px',
              }}
            >
              <TaskAltIcon sx={{ color: '#1C3045', mr: 1, fontSize: '20px' }} />
              Scan Complete
            </Typography>
          ) : (
            <ScanBlockedTooltip reason={scanBlockedReason}>
              <Button
                variant="contained"
                disableRipple
                aria-label={`Find matches for ${fundingNeed.title}`}
                sx={{
                  m: 0.5,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  color: 'white',
                  bgcolor: '#1C3045',
                  boxShadow: 'none',
                  borderRadius: 2,
                  width: 'calc(100% - 8px)',
                  height: '40px',
                  '&:hover': { backgroundColor: '#162433' },
                }}
                disabled={!canStartScan}
                onClick={() => {
                  handleRunScan(String(fundingNeed.id), includeRelational)
                }}
              >
                Find Matches
              </Button>
            </ScanBlockedTooltip>
          )}
        </Box>
      </Box>

      {/* Upgrade dialog for 402 errors */}
      <ScanStatusAnnouncer
        message={scanAnnouncement({
          title: fundingNeed.title,
          isScanning,
          scanError,
          latestStatus: needId ? latestStatusForNeed(needId) : 'idle',
          latestScanFoundNothing,
        })}
      />

      <UpgradeCTA
        variant="dialog"
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        message={
          upgradeReason ||
          'Free tier users are limited to 2 scans. Please upgrade to continue.'
        }
      />
    </ListItem>
  )
}
